import { type MouseEvent } from "react";
import { Button } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCoordinator } from "@/shared/lib/hooks";
import "@/entities/journal";
import { Can } from "@shared/lib/casl";
import { useAppRoutes } from "@shared/lib/react-router";
import {
  SectionFourActionEnum,
  SectionFourActionTypes,
} from "../../../../../../model";
import "./section-fourth-actions.scss";

interface SectionFourthActionsProps {
  canSendToSign: boolean;
  // canRejectSign: boolean;
  // canRollback: boolean;
  // canDelete: boolean;
  // canEdit: boolean;
  canViewVersions: boolean;
  itemNoVersionId?: string | null;
  itemInfo?: any;
  actions: string[];
  actionsPartTwo: string[];
  isPartTwoSelected: boolean;
  handleIsForm: (
    status: boolean,
    event?: MouseEvent<HTMLSpanElement>,
  ) => void;
  handleCloseInfo: () => void;
  sectionTwoAction?: SectionFourActionEnum;
  handleSendToSign?: (params: { itemNoVersionId: string; itemPartOneId: string }) => Promise<void> | void;
  handleSendToSignTwo?: (params: { itemNoVersionId: string; itemPartTwoId: string }) => Promise<void> | void;
  handleReturn?: (params: { itemNoVersionId: string; itemPartOneId: string }) => Promise<void> | void;
  handleReturnTwo?: (params: { itemNoVersionId: string; itemPartTwoId: string }) => Promise<void> | void;
  handleStatusReturn?: (params: { itemNoVersionId: string; itemPartOneId: string; comment: string }) => Promise<void> | void;
  handleStatusReturnTwo?: (params: { itemNoVersionId: string; itemPartTwoId: string; comment: string }) => Promise<void> | void;
  handleDelete?: (params: { itemNoVersionId: string }) => void;
}

export const SectionFourthActions = ({
  canSendToSign,
  // canRejectSign,
  // canRollback,
  // canDelete,
  // canEdit,
  canViewVersions,
  itemNoVersionId,
  itemInfo,
  actions,
  actionsPartTwo,
  isPartTwoSelected,
  handleIsForm,
  handleCloseInfo,
  sectionTwoAction,
  handleSendToSign: handleSendToSignProp,
  handleSendToSignTwo: handleSendToSignTwoProp,
  handleReturn: handleReturnProp,
  handleReturnTwo: handleReturnTwoProp,
  handleStatusReturn: handleStatusReturnProp,
  handleStatusReturnTwo: handleStatusReturnTwoProp,
  handleDelete: handleDeleteProp,
}: SectionFourthActionsProps) => {
  const navigate = useNavigate();
  const appRoutes = useAppRoutes();
  const { projectId, objectId, mainJournalId, titleId } = useCoordinator();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClose = (
    event: MouseEvent<HTMLSpanElement>,
  ) => {
    handleIsForm(false, event);
    handleCloseInfo();
  };

  const handleEdit = () => {
    if (itemInfo?.itemPartTwo?.status) {
    // eslint-disable-next-line no-unused-expressions
    } else {
      // Navigate to form page
      navigate(
        appRoutes.journals.section4Form(projectId, objectId, mainJournalId, titleId, itemNoVersionId || ""),
      );
    }
  };

  const handleVersions = () => {
    const params = new URLSearchParams(searchParams);
    params.set("itemNoVersionId", itemNoVersionId || "");
    params.set("action", "VERSION");
    setSearchParams(params);
  };

  const handleSendToSign = async () => {
    if (!itemNoVersionId) return;
    // choose part: prefer part two if it can be sent
    const usePartTwo = actionsPartTwo.includes("sendToSign");
    if (usePartTwo && handleSendToSignTwoProp && itemInfo?.itemPartTwo?.id) {
      await handleSendToSignTwoProp({
        itemNoVersionId,
        itemPartTwoId: itemInfo.itemPartTwo.id,
      });
    } else if (handleSendToSignProp && itemInfo?.itemPartOne?.id) {
      await handleSendToSignProp({
        itemNoVersionId,
        itemPartOneId: itemInfo.itemPartOne.id,
      });
    } else {
      const params = new URLSearchParams(searchParams);
      if (usePartTwo) {
        params.set("itemPartTwo", itemInfo?.itemPartTwo?.id || "");
        params.delete("itemPartOne");
      } else {
        params.set("itemPartOne", itemInfo?.itemPartOne?.id || "");
        params.delete("itemPartTwo");
      }
      setSearchParams(params);
    }
  };

  const handleRejectSign = async () => {
    if (!itemNoVersionId) return;
    if (isPartTwoSelected && handleStatusReturnTwoProp && itemInfo?.itemPartTwo?.id) {
      await handleStatusReturnTwoProp({
        itemNoVersionId,
        itemPartTwoId: itemInfo.itemPartTwo.id,
        comment: "",
      });
    } else if (handleStatusReturnProp && itemInfo?.itemPartOne?.id) {
      await handleStatusReturnProp({
        itemNoVersionId,
        itemPartOneId: itemInfo.itemPartOne.id,
        comment: "",
      });
    } else {
      const params = new URLSearchParams(searchParams);
      if (isPartTwoSelected) {
        params.set("itemPartTwo", itemInfo?.itemPartTwo?.id || "");
        params.delete("itemPartOne");
      } else {
        params.set("itemPartOne", itemInfo?.itemPartOne?.id || "");
        params.delete("itemPartTwo");
      }
      setSearchParams(params);
    }
  };

  const handleRollback = async () => {
    if (!itemNoVersionId) return;
    if (isPartTwoSelected && handleReturnTwoProp && itemInfo?.itemPartTwo?.id) {
      await handleReturnTwoProp({
        itemNoVersionId,
        itemPartTwoId: itemInfo.itemPartTwo.id,
      });
    } else if (handleReturnProp && itemInfo?.itemPartOne?.id) {
      await handleReturnProp({
        itemNoVersionId,
        itemPartOneId: itemInfo.itemPartOne.id,
      });
    } else {
      const params = new URLSearchParams(searchParams);
      if (isPartTwoSelected) {
        params.set("itemPartTwo", itemInfo?.itemPartTwo?.id || "");
        params.delete("itemPartOne");
      } else {
        params.set("itemPartOne", itemInfo?.itemPartOne?.id || "");
        params.delete("itemPartTwo");
      }
      setSearchParams(params);
    }
  };

  const handleDelete = () => {
    if (!itemNoVersionId) return;
    if (handleDeleteProp) {
      handleDeleteProp({ itemNoVersionId });
      // Navigate back after deletion
      if (projectId && objectId && mainJournalId && titleId) {
        navigate(
          appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
        );
      }
    } else {
      const params = new URLSearchParams(searchParams);
      if (itemInfo?.itemPartTwo?.status) {
        params.set("itemPartTwo", itemInfo?.itemPartTwo?.id || "");
        params.delete("itemPartOne");
      } else {
        params.set("itemPartOne", itemInfo?.itemPartOne?.id || "");
        params.delete("itemPartTwo");
      }
      setSearchParams(params);
    }
  };

  // Показываем кнопки только если НЕ в режиме редактирования второй части
  if (sectionTwoAction === SectionFourActionTypes.EDIT) {
    return null;
  }

  return (
    <div className="journal-actions-bar journal-actions-bar--spaced">
      {canSendToSign && (
        <Can I="edit" a="section_4">
          <Button style={{ height: 44 }} onClick={handleSendToSign}>
            Отправить на подписание
          </Button>
        </Can>
      )}
      {actions.includes("rejectSign") && (
        <Can I="edit" a="section_4">
          <Button style={{ height: 44 }} onClick={handleRejectSign}>
            Отозвать с подписания
          </Button>
        </Can>
      )}
      {actions.includes("rollback") && (
        <Can I="edit" a="section_4">
          <Button style={{ height: 44 }} onClick={handleRollback}>
            Вернуть в работу
          </Button>
        </Can>
      )}

      <Can I="edit" a="section_4">
        {actions.includes("delete") && (
          <Button style={{ height: 44 }} onClick={handleDelete}>
            Удалить
          </Button>
        )}
      </Can>
      {canViewVersions && (
        <Button style={{ height: 44 }} onClick={handleVersions}>
          Версии
        </Button>
      )}
      {actions.includes("edit") && (
        <Can I="edit" a="section_4">
          <Button style={{ height: 44 }} onClick={handleEdit}>
            Редактировать
          </Button>
        </Can>
      )}
      <Button
        type="default"
        onClick={handleClose}
        style={{ height: 44, marginBottom: 16 }}
      >
        Закрыть
      </Button>
    </div>
  );
};
